import { useEffect, useRef } from 'react';
import type { Session } from '@supabase/supabase-js';
import { clearSharedActivity, IDLE_NOTICE_KEY, RECEPTION_IDLE_MS, sessionKeyFromToken, watchIdle } from './idleSignOut';

/**
 * Follow-up F4 — decision 11. Arms the 30-minute idle sign-out while the
 * signed-in user is working as RECEPTION on the selected property (a user
 * who is owner of one property and reception of another is timed out only
 * while the reception property is selected). Owner sessions are never armed.
 *
 * `signOut` must be the plain session sign-out, NOT the guarded one: the
 * unsynced-events modal would block an unattended desk forever. Unsynced
 * desk events are safe without it — on SIGNED_OUT the desk engine keeps them
 * encrypted in IndexedDB behind its "signed out with unsynced desk events"
 * blocker and resumes them when the same account signs back in
 * (DeskEngine.handleSignedOut / start).
 */
export function useReceptionIdleSignOut(
  role: 'owner' | 'reception' | null,
  session: Session | null,
  signOut: () => Promise<unknown>,
  timeoutMs: number = RECEPTION_IDLE_MS,
): void {
  const signOutRef = useRef(signOut);
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  const userId = session?.user.id ?? null;
  const sessionKey = userId ? sessionKeyFromToken(session?.access_token, userId) : null;
  const armed = role === 'reception' && sessionKey !== null;

  useEffect(() => {
    if (!armed || !sessionKey) return;
    return watchIdle({
      sessionKey,
      timeoutMs,
      onIdle: () => {
        try {
          sessionStorage.setItem(IDLE_NOTICE_KEY, '1');
        } catch {
          /* the notice is a courtesy */
        }
        clearSharedActivity();
        void signOutRef.current();
      },
    });
  }, [armed, sessionKey, timeoutMs]);
}

/** Whether the last sign-out was the idle one (for the sign-in screen). */
export function readIdleSignOutNotice(): boolean {
  try {
    return sessionStorage.getItem(IDLE_NOTICE_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearIdleSignOutNotice(): void {
  try {
    sessionStorage.removeItem(IDLE_NOTICE_KEY);
  } catch {
    /* ignore */
  }
}
